import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { planetColors } from '../../utils/astroUtils';
import { useLanguage } from '../../context/LanguageContext';

export const planetSymbols = {
  Sun:"☉", Moon:"☽", Mars:"♂", Mercury:"☿", Jupiter:"♃",
  Venus:"♀", Saturn:"♄", Rahu:"☊", Ketu:"☋", "Ascendant (Lagna)":"↑",
};

/* ─── Tooltip — positioned absolutely inside the clock-wrapper ─── */
const Tooltip = ({ planet, data, angle, svgRef, containerRef, isLive }) => {
  const { t } = useLanguage();
  if (!svgRef?.current || !containerRef?.current) return null;

  const color = planetColors[planet] || '#FFF';
  const sym   = planetSymbols[planet] || planet.slice(0, 2);
  const pc  = p => planetColors[p]  || '#aaa';
  const ps  = p => planetSymbols[p] || '';
  const ti  = data.transitInfo;

  // Convert SVG tip coords → container-relative coords
  const svgRect = svgRef.current.getBoundingClientRect();
  const containerRect = containerRef.current.getBoundingClientRect();

  // Tip coordinate in SVG viewBox system
  const rad    = (angle - 90) * (Math.PI / 180);
  const tipSVGX = 400 + (380 - 15) * Math.cos(rad);
  const tipSVGY = 425 + (380 - 15) * Math.sin(rad);

  // SVG viewBox dimensions
  const vbW = 880; // viewBox width (-40 to 840)
  const vbH = 900; // viewBox height (-40 to 860)
  const vbX = -40; // viewBox minX
  const vbY = -40; // viewBox minY

  // Because preserveAspectRatio="xMidYMid meet"
  const scale = Math.min(svgRect.width / vbW, svgRect.height / vbH);
  
  // Calculate letterboxing offsets
  const offsetX = (svgRect.width - (vbW * scale)) / 2;
  const offsetY = (svgRect.height - (vbH * scale)) / 2;

  // Actual screen coordinates of the tip
  const tipScreenX = svgRect.left + offsetX + (tipSVGX - vbX) * scale;
  const tipScreenY = svgRect.top  + offsetY + (tipSVGY - vbY) * scale;

  const isMobile = window.innerWidth < 420;
  const W = isMobile ? Math.min(window.innerWidth - 16, 190) : 260;
  const isTop = tipScreenY < window.innerHeight / 2;

  let left = tipScreenX + window.scrollX - W / 2;
  let top  = isTop
    ? tipScreenY + window.scrollY + 16
    : tipScreenY + window.scrollY - 16;

  // Hard-clamp inside window on all sides
  left = Math.max(4 + window.scrollX, Math.min(window.scrollX + window.innerWidth - W - 4, left));
  top  = Math.max(4 + window.scrollY, top);

  const PAD    = isMobile ? '6px 9px' : '10px 13px';
  const TITLE_FS = isMobile ? '0.78rem' : '0.9rem';
  const FS     = isMobile ? '0.6rem'  : '0.72rem';
  const FS_LBL = isMobile ? '0.52rem' : '0.68rem';
  const MAX_H  = containerRect.height * 0.72;

  /* Row: stacked on mobile (label above value), inline on desktop */
  const Row = ({ label, value, vc }) =>
    isMobile ? (
      <div style={{ marginBottom:'4px' }}>
        <div style={{ color:'#8a8a9a', fontSize:FS_LBL, lineHeight:1.2 }}>{label}</div>
        <div style={{ color:vc||'#fff', fontWeight:700, fontSize:FS, wordBreak:'break-word', lineHeight:1.3 }}>{value||'–'}</div>
      </div>
    ) : (
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:FS,
        marginBottom:'3px', gap:'6px', alignItems:'flex-start' }}>
        <span style={{ color:'#9ca3af', whiteSpace:'nowrap', flexShrink:0 }}>{label}</span>
        <span style={{ color:vc||'#fff', fontWeight:700, textAlign:'right', wordBreak:'break-word' }}>{value||'–'}</span>
      </div>
    );

  /* LordRow: compact pill on both, shorter labels on mobile */
  const LordRow = ({ label, shortLabel, lord }) => (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
      fontSize:FS, marginBottom:'2px', gap:'4px' }}>
      <span style={{ color:'#8a8a9a', flexShrink:0, fontSize:FS_LBL }}>
        {isMobile ? shortLabel : label}
      </span>
      <span style={{ color:pc(lord), background:`${pc(lord)}22`,
        border:`1px solid ${pc(lord)}55`, padding:'1px 5px', fontWeight:700, fontSize:FS,
        whiteSpace:'nowrap' }}>
        {ps(lord)} {t(lord)||'–'}
      </span>
    </div>
  );

  const Div = () => <div style={{ borderTop:'1px solid rgba(255,255,255,0.1)', margin:'5px 0' }} />;

  const transformOrigin = isTop ? 'top center' : 'bottom center';

  return ReactDOM.createPortal(
    <div style={{
      position:'absolute', left, top, width:W, zIndex:9999,
      background:'rgba(4,4,14,0.97)',
      border:`1.5px solid ${color}`,
      padding:PAD,
      fontFamily:'Inter,sans-serif',
      boxShadow:`0 0 20px ${color}44, 0 6px 24px rgba(0,0,0,0.95)`,
      pointerEvents:'none',
      overflow:'hidden',
      maxHeight: MAX_H,
      boxSizing:'border-box',
      transformOrigin,
      transform: isTop ? 'none' : 'translateY(-100%)',
    }}>
      {/* Planet header */}
      <div style={{ color, fontWeight:800, fontSize:TITLE_FS, marginBottom:'5px',
        borderBottom:`1px solid ${color}44`, paddingBottom:'4px' }}>
        {sym} {t(planet)}
      </div>

      {/* Core data */}
      <Row label={t('Rashi')}     shortLabel={t('Rashi')} value={t(data.rashi)}         vc={color} />
      <Row label={t('Nakshatra')} shortLabel={t('Nak')}   value={t(data.nakshatra)}      vc="#FF8FAB" />
      <Row label={t('Pada')}      shortLabel={t('Pada')}  value={data.pada}           vc="#76D7C4" />
      <Row label={t('Degree')}    shortLabel={t('Deg')}   value={data.rashiDegreeDMS} vc={color} />
      {data.conditions?.length > 0 &&
        <Row label={t('Status')} shortLabel={t('Status')} value={data.conditions.map(c => t(c)).join(' · ')} vc="#f39c12" />}

      {/* Lords */}
      <Div />
      <LordRow label={`${t('SL')}  · ${t('Sign Lord')}`}    shortLabel={t('Sign Lord')} lord={data.signLord} />
      <LordRow label={`${t('NL')}  · ${t('Nakshatra Lord')}`} shortLabel={t('Nakshatra Lord')}  lord={data.nakshatraLord} />
      <LordRow label={`${t('SBL')} · ${t('Sub Lord')}`}     shortLabel={t('Sub Lord')}  lord={data.subLord} />
      {!isMobile && <LordRow label={`${t('SSL')} · ${t('Sub Sub Lord')}`} shortLabel={t('SSL')} lord={data.subSubLord} />}

      {/* Transit — desktop only */}
      {!isMobile && isLive && ti && (
        <>
          <Div />
          <Row label={`${t('Entered')} (S) ${t(ti.currentRashi)}`} value={ti.enteredRashi}  vc="#F4A223" />
          <Row label={`${t('Next')} (S) ${t(ti.nextRashi)}`}        value={ti.nextRashiTime} vc="#E74C3C" />
          <Row label={`${t('Entered')} (N) ${t(ti.currentNak)}`}   value={ti.enteredNak}    vc="#C0D8F0" />
          <Row label={`${t('Next')} (N) ${t(ti.nextNak)}`}          value={ti.nextNakTime}   vc="#76D7C4" />
        </>
      )}
    </div>,
    document.body
  );
};


/* ─── ClockHands ──────────────────────────────────────── */
const ClockHands = ({ center, radius, planetAngles={}, planetData={}, isLive=false, svgRef, containerRef }) => {
  const { t } = useLanguage();
  const [hovered, setHovered] = useState(null);
  const [pinned,  setPinned]  = useState(null);
  const leaveTimer = useRef(null);

  const handleEnter = useCallback((planet) => {
    clearTimeout(leaveTimer.current);
    setHovered(planet);
  }, []);

  const handleLeave = useCallback(() => {
    leaveTimer.current = setTimeout(() => setHovered(null), 150);
  }, []);

  if (!planetAngles || Object.keys(planetAngles).length === 0) return null;
  const active = pinned || hovered;

  return (
    <>
      {Object.entries(planetAngles).map(([planet, angle]) => {
        const color   = planetColors[planet] || '#FFF';
        const tipY    = center - (radius - 15);
        const smallCy = center - (radius - 40);
        const bigCy   = center - (radius - 80);
        const bigR=13, smallR=9, shaftW=2.5, tipGap=8;
        const arrowBaseY = smallCy - smallR - tipGap;
        const arrow = `M ${center},${arrowBaseY} Q ${center+6},${arrowBaseY-4} ${center},${tipY} Q ${center-6},${arrowBaseY-4} ${center},${arrowBaseY} Z`;
        const sym  = planetSymbols[planet] || planet.slice(0,2);
        // Use translated abbreviation: 'As' for Ascendant, else first 2 chars of planet name passed through t()
        const rawAcro = planet === 'Ascendant (Lagna)' ? 'As' : planet.slice(0,2);
        const acro = t(rawAcro);
        const isAct = active === planet;

        return (
          <g key={planet}
            style={{ filter:`drop-shadow(0 0 ${isAct?10:4}px ${color})` }}
            onMouseEnter={() => handleEnter(planet)}
            onMouseLeave={handleLeave}
            onClick={() => setPinned(pinned === planet ? null : planet)}
          >
            <g transform={`rotate(${angle},${center},${center})`} style={{ cursor:'pointer' }}>
              <line x1={center} y1={center} x2={center} y2={tipY} stroke="transparent" strokeWidth={28} />
              <circle cx={center} cy={center} r={4} fill={color} />
              <line x1={center} y1={center} x2={center} y2={bigCy+bigR} stroke={color} strokeWidth={shaftW} />
              <circle cx={center} cy={bigCy} r={bigR}
                fill={isAct?`${color}33`:'rgba(0,0,0,0.8)'} stroke={color} strokeWidth={shaftW} />
              <line x1={center} y1={bigCy-bigR} x2={center} y2={smallCy+smallR} stroke={color} strokeWidth={shaftW} />
              <circle cx={center} cy={smallCy} r={smallR}
                fill={isAct?`${color}33`:'rgba(0,0,0,0.8)'} stroke={color} strokeWidth={shaftW} />
              <line x1={center} y1={smallCy-smallR} x2={center} y2={arrowBaseY} stroke={color} strokeWidth={shaftW} />
              <path d={arrow} fill={color} />
              <text x={center} y={bigCy} fill={color} fontSize="12" fontWeight="bold"
                textAnchor="middle" dominantBaseline="central"
                transform={`rotate(${-angle},${center},${bigCy})`}
                style={{ fontFamily:'Inter,sans-serif', pointerEvents:'none' }}>{acro}</text>
              <text x={center} y={smallCy} fill={color}
                fontSize={planet==='Ascendant (Lagna)'?'10':'15'}
                textAnchor="middle" dominantBaseline="central"
                transform={`rotate(${-angle},${center},${smallCy})`}
                style={{ fontFamily:'Arial,sans-serif', pointerEvents:'none' }}>{sym}</text>
            </g>
          </g>
        );
      })}

      {active && planetData[active] && (
        <Tooltip
          planet={active}
          data={planetData[active]}
          angle={planetAngles[active]}
          svgRef={svgRef}
          containerRef={containerRef}
          isLive={isLive}
        />
      )}
    </>
  );
};

export default ClockHands;
